
import { computed, defineComponent, PropType, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import useContentModule from '@/pages/composables/useContentModule'
import { CtCatalogue, CtCatalogueProduct, CtProduct } from '@/models/interfaces/content-type'
import useDiffSaveHandler, { SaveHooks } from '@/pages/Manage/Catalogues/composables/useDiffSaveHandler'
import { ContentType } from '@/services/directr'
import { cloneDeep } from 'lodash'
import ChannelProductCard from '@/pages/Products/ProductDetail/components/ChannelProductCard.vue'
import { Entry, generateUUID, UUID } from '@Protium/vue-ozone-utils'
import CloseIcon from '@/components/Icons/CloseIcon.vue'
import TickIcon from '@/components/Icons/TickIcon.vue'
import ListHeader from '@/components/ListHeader.vue'
import { uuid } from '@/utils'
import AddIcon from '@/components/Icons/AddIcon.vue'
import DButton from '@/components/base/DButton.vue'
import LockClosedIcon from '@/components/Icons/LockClosedIcon.vue'

export default defineComponent({
  name: 'ChannelInformation',
  props: {
    product: {
      type: Object as PropType<CtProduct>,
      required: true,
    },
    locked: {
      type: Boolean,
      default: false
    }
  },
  components: { LockClosedIcon, DButton, AddIcon, ListHeader, TickIcon, CloseIcon, ChannelProductCard },
  setup (props) {

    const $store = useStore()
    const $router = useRouter()
    const $directr = $store.getters['Services/Directr']
    const $q = useQuasar()

    const editing = ref(false)

    const removeChannelProduct = (channelProduct) => {
      if (channelProduct.sys?.draft) {
        diffState.rhs = diffState.rhs.filter(cp => cp.sys.id !== channelProduct.sys.id)
      } else {
        diffState.rhs = diffState.rhs.map(cp => {
          if (cp.sys.id === channelProduct.sys.id) {
            return { ...cp, delete: true }
          }
          return cp
        })
        editing.value = true
      }
    }

    const {
      items: catalogueProducts,
      contentType: catalogueProductContentType,
      fetch: fetchCatalogueProducts,
    } = useContentModule<CtCatalogueProduct>('CatalogueProducts', $store)

    const {
      items: catalogues,
      model: catalogueModel,
      requestHandler: catalogueRequestHandler
    } = useContentModule<CtCatalogue>('Catalogues', $store)

    const saveHooks: SaveHooks = {}
    const {
      diffState,
      currentDiff,
      save: saveDiffState
    } = useDiffSaveHandler<CtCatalogueProduct>(ContentType.CatalogueProduct, $directr, saveHooks, [])

    const fetchCatalogues = async (catProducts) => {
      const { items } = await catalogueRequestHandler.get(
        ContentType.Catalogue,
        [catProducts.map(cp => cp.catalogue.toString()), { select: ['id', 'name', 'currency', 'service'] }]
      )
      catalogues.value = items
    }

    if (props.product?.sys?.id) {
      void fetchCatalogueProducts([{ field: 'id', method: 'is', match: props.product.sys.id }]).then(async (items) => {
        await fetchCatalogues(items)
        diffState.lhs = cloneDeep(items)
        diffState.rhs = cloneDeep(items)
      })
    }

    const catalogueMapping = computed(
      () => catalogues.value.reduce((prev, catalogue) => ({ ...prev, [catalogue.sys.id]: catalogue }), {})
    )

    // void fetch([{}]).then(items => {
    //   diffState.lhs = cloneDeep(items) ?? []
    //   diffState.rhs = cloneDeep(items) ?? []
    // })

    // const channels = computed(() => diffState.rhs?.filter(c => c.client === props.client.sys.id))


    const cancel = () => {
      diffState.rhs = cloneDeep(diffState.lhs)
      editing.value = false
    }

    const save = async () => {
      await saveDiffState()
      const items = await fetchCatalogueProducts([{ field: 'id', method: 'is', match: props.product.sys.id }])
      diffState.lhs = cloneDeep(items)
      diffState.rhs = cloneDeep(items)
      await fetchCatalogues(items)
      editing.value = false
    }

    const channelDialog = reactive({
      show: false,
      collection: [] as CtCatalogue[],
      selection: [] as UUID[],
      select: (item: Entry) => {
        const index = channelDialog.selection.findIndex(s => s === item?.sys?.id)
        if (index >= 0) {
          channelDialog.selection.splice(index, 1)
        } else {
          channelDialog.selection.push(item.sys?.id)
        }
      },
      model: catalogueModel,
      requestHandler: catalogueRequestHandler,
    })

    const fetchUnlinkedCatalogues = async () => {
      const linkedChannels = diffState.rhs.map(cp => cp.catalogue)
      const { items = [] } = await catalogueRequestHandler.get(
        ContentType.Catalogue,
        [{ select: ['id', 'name'] }]
      )
      channelDialog.collection = items.filter((c: CtCatalogue) => !linkedChannels.includes(c.sys.id))
    }
    void fetchUnlinkedCatalogues()

    watch(() => channelDialog.show, fetchUnlinkedCatalogues)

    const catalogueProductDraft = (cSysId) => ({
      sys: { id: uuid(), draft: true, type: catalogueProductContentType },
      id: props.product.sys.id,
      catalogue: cSysId as UUID,
      // title: product.title,
      price: 0,
      active: true
    })

    const addChannelDialogSelection = () => {
      if (channelDialog.selection.length > 0) {
        editing.value = true
        channelDialog.selection.forEach(cSysId => {
          diffState.rhs.push(catalogueProductDraft(cSysId))
        })
        void fetchCatalogues(diffState.rhs)
        channelDialog.show = false
        channelDialog.selection = []
      }
    }

    const updateProductPrice = (catalogueProduct, event) => {
      catalogueProduct.price = !isNaN(parseFloat(event)) ? parseFloat(event) : event
      editing.value = true
    }

    return {
      editing,
      catalogues,
      diffState,
      catalogueMapping,
      removeChannelProduct,
      cancel,
      save,
      channelDialog,
      addChannelDialogSelection,
      updateProductPrice,
    }
  },
})
